import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  Tabs,
  Tab,
  Paper,
  Grid,
} from "@mui/material";
import { toast } from "react-toastify";
import { rfqService } from "../../services/rfq.service";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
} from "../../../../app.interface";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";
import { useAuth } from "../../../auth/hooks/useAuth";
import {
  RFQ,
  RFQStatus,
  ListRFQsRequest,
  RFQActivityLog,
} from "../../types/rfq.types";
import { RFQFormDialog } from "./RFQFormDialog";
import { VendorQuotationsDialog } from "./VendorQuotationsDialog";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { RFQActivityTimeline } from "../../components/ActivityTimeline/RFQActivityTimeline";
import { hasPermission } from "../../../auth/utils/permission.utils";

interface StatusConfig {
  label: string;
  color: string;
  icon: string;
  bgColor: string;
  canEdit: boolean;
  canDelete: boolean;
  canSend?: boolean;
  canCancel?: boolean;
  canManageQuotations?: boolean;
}

const statusConfig: Record<RFQStatus, StatusConfig> = {
  draft: {
    label: "Draft",
    color: "#64748B",
    icon: "edit",
    bgColor: "#F1F5F9",
    canEdit: true,
    canDelete: true,
    canSend: true,
  },
  sent: {
    label: "Sent",
    color: "#3B82F6",
    icon: "send",
    bgColor: "#EFF6FF",
    canEdit: false,
    canDelete: false,
    canCancel: true,
    canManageQuotations: true,
  },
  in_progress: {
    label: "In Progress",
    color: "#F59E0B",
    icon: "hourglass_empty",
    bgColor: "#FFFBEB",
    canEdit: false,
    canDelete: false,
    canCancel: true,
    canManageQuotations: true,
  },
  completed: {
    label: "Completed",
    color: "#059669",
    icon: "check_circle",
    bgColor: "#ECFDF5",
    canEdit: false,
    canDelete: false,
  },
  cancelled: {
    label: "Cancelled",
    color: "#DC2626",
    icon: "cancel",
    bgColor: "#FEF2F2",
    canEdit: false,
    canDelete: false,
  },
};

export const RFQList: React.FC = () => {
  const [rfqs, setRfqs] = React.useState<RFQ[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [quotationsDialogOpen, setQuotationsDialogOpen] = React.useState(false);
  const [selectedRFQ, setSelectedRFQ] = React.useState<RFQ | null>(null);
  const [selectedRFQDetails, setSelectedRFQDetails] =
    React.useState<RFQ | null>(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuRFQId, setMenuRFQId] = React.useState<string | null>(null);

  // Activity logs states
  const [activityLogs, setActivityLogs] = React.useState<RFQActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const [activityTotal, setActivityTotal] = React.useState(0);
  const [activityPage, setActivityPage] = React.useState(1);
  const [activityPageSize] = React.useState(10);

  // Drawer tab state
  const [drawerTab, setDrawerTab] = React.useState(0);

  const { user } = useAuth();

  // Load activity logs for selected RFQ (from embedded data)
  const loadActivityLogs = (rfq: RFQ, page: number = 1) => {
    setActivityLoading(true);
    try {
      const response = rfqService.getRFQActivityLogs(
        rfq,
        page - 1, // Convert to 0-based indexing
        activityPageSize
      );

      setActivityLogs(response.activities || []);
      setActivityTotal(response.totalCount || 0);
      setActivityPage(page);
    } catch (error: any) {
      console.error("Error loading activity logs:", error);
      toast.error("Failed to load activity history");
    } finally {
      setActivityLoading(false);
    }
  };

  const handleActivityPageChange = (page: number) => {
    if (selectedRFQDetails) {
      loadActivityLogs(selectedRFQDetails, page);
    }
  };

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter RFQs by their current status",
      options: [
        {
          id: "draft",
          label: "Draft",
          value: "draft",
          icon: "edit",
          color: "#64748B",
          description: "RFQs in draft state",
        },
        {
          id: "sent",
          label: "Sent",
          value: "sent",
          icon: "send",
          color: "#3B82F6",
          description: "RFQs sent to suppliers",
        },
        {
          id: "in_progress",
          label: "In Progress",
          value: "in_progress",
          icon: "hourglass_empty",
          color: "#F59E0B",
          description: "RFQs receiving quotations",
        },
        {
          id: "completed",
          label: "Completed",
          value: "completed",
          icon: "check_circle",
          color: "#059669",
          description: "Completed RFQs",
        },
        {
          id: "cancelled",
          label: "Cancelled",
          value: "cancelled",
          icon: "cancel",
          color: "#DC2626",
          description: "Cancelled RFQs",
        },
      ],
    },
  ];

  const getStatusChip = (status: RFQStatus) => {
    const config = statusConfig[status];
    return (
      <Chip
        label={config.label}
        icon={<Icon sx={{ fontSize: 16 }}>{config.icon}</Icon>}
        sx={{
          bgcolor: config.bgColor,
          color: config.color,
          fontWeight: 600,
          fontSize: 12,
          height: 28,
          "& .MuiChip-icon": {
            color: config.color,
          },
        }}
      />
    );
  };

  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const MasterColumns: TableColumn[] = [
    {
      id: "rfq_number",
      header: "RFQ Number",
      field: "rfq_number",
      type: "action",
      width: "180px",
      sortable: true,
      renderAction: (row) => {
        const isDraft = row.status === "draft" || !row.rfq_number;
        return isDraft ? (
          <Tooltip
            title="Send this RFQ to suppliers to generate an RFQ Number."
            arrow
          >
            <Chip
              label="Not Generated Yet"
              size="small"
              sx={{
                bgcolor: "grey.100",
                color: "grey.600",
                fontWeight: 500,
                fontSize: "0.95em",
                height: 24,
                px: 1.5,
                letterSpacing: 0.2,
                borderRadius: 1,
                cursor: "pointer",
              }}
            />
          </Tooltip>
        ) : (
          <Typography fontWeight={600} sx={{ fontSize: "0.875rem" }}>
            {row.rfq_number}
          </Typography>
        );
      },
    },
    {
      id: "purchase_request_number",
      header: "PR Number",
      field: "purchase_request_number",
      type: "text",
      width: "150px",
      sortable: true,
    },
    {
      id: "supplier_count",
      header: "Suppliers",
      field: "supplier_count",
      type: "action",
      width: "120px",
      renderAction: (row) => (
        <Typography fontWeight={600}>
          {row.selected_suppliers?.length || 0} suppliers
        </Typography>
      ),
    },
    {
      id: "quotations_count",
      header: "Quotations",
      field: "quotations_count",
      type: "action",
      width: "120px",
      renderAction: (row) => (
        <Typography fontWeight={600}>
          {row.vendor_quotations?.length || 0} received
        </Typography>
      ),
    },
    {
      id: "due_date",
      header: "Due Date",
      field: "due_date",
      type: "action",
      width: "120px",
      sortable: true,
      renderAction: (row) => (
        <Typography>
          {row.due_date ? formatDate(row.due_date) : "Not set"}
        </Typography>
      ),
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      type: "action",
      width: "140px",
      sortable: true,
      renderAction: (row) => getStatusChip(row.status),
    },
    {
      id: "created_at",
      header: "Created",
      field: "created_at",
      type: "action",
      width: "120px",
      sortable: true,
      renderAction: (row) => (
        <Typography>{formatDate(row.created_at)}</Typography>
      ),
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: WithId & Record<string, any>) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
            setMenuRFQId(row._id);
          }}
          sx={{
            width: 34,
            height: 34,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            },
          }}
        >
          <Icon sx={{ fontSize: 18, color: "primary.main" }}>more_vert</Icon>
        </IconButton>
      ),
    },
  ];

  const loadRFQs = async () => {
    setLoading(true);
    try {
      const params: ListRFQsRequest = {
        searchQuery: search,
        sortField,
        sortOrder,
        pageIndex: page,
        pageSize,
        filters: Object.keys(filters).map((key) => ({
          field: key,
          value: filters[key],
          operator: "equals" as const,
        })),
      };

      const response = await rfqService.listRFQs(params);

      if (response.success && response.data) {
        setRfqs(response.data.rfqs || []);
        setTotalItems(response.data.totalCount || 0);
      } else {
        throw new Error(response.error?.message || "Failed to load RFQs");
      }
    } catch (error: any) {
      console.error("Error loading RFQs:", error);
      toast.error(error.message || "Failed to load RFQs");
      setRfqs([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRFQ = async (rfqId: string) => {
    try {
      const response = await rfqService.sendRFQ({ _id: rfqId });
      if (response.success) {
        toast.success("RFQ sent to suppliers successfully");
        loadRFQs();
      } else {
        throw new Error(response.error?.message || "Failed to send RFQ");
      }
    } catch (error: any) {
      console.error("Error sending RFQ:", error);
      // Extract backend error message if available
      const backendMsg = error?.response?.data?.error?.message;
      const msg = backendMsg || error?.message || "Failed to send RFQ";
      if (msg.includes("already has an active RFQ")) {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      handleMenuClose();
    }
  };

  const handleCancelRFQ = async (rfqId: string) => {
    try {
      const response = await rfqService.cancelRFQ({ _id: rfqId });
      if (response.success) {
        toast.success("RFQ cancelled successfully");
        loadRFQs();
      } else {
        throw new Error(response.error?.message || "Failed to cancel RFQ");
      }
    } catch (error: any) {
      console.error("Error cancelling RFQ:", error);
      toast.error(error.message || "Failed to cancel RFQ");
    } finally {
      handleMenuClose();
    }
  };

  const handleDeleteRFQ = async () => {
    if (!selectedRFQ) return;
    try {
      const response = await rfqService.deleteRFQ({ _id: selectedRFQ._id });
      if (response.success) {
        toast.success("RFQ deleted successfully");
        loadRFQs();
      } else {
        throw new Error(response.error?.message || "Failed to delete RFQ");
      }
    } catch (error: any) {
      console.error("Error deleting RFQ:", error);
      toast.error(error.message || "Failed to delete RFQ");
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (row: WithId & Record<string, any>) => {
    const rfq = rfqs.find((r) => r._id === row._id);
    setSelectedRFQDetails(rfq || null);
    setDrawerTab(0); // Reset to first tab
    if (rfq) {
      loadActivityLogs(rfq, 1); // Load first page of activity logs
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRFQId(null);
  };

  const handleOpenFormDialog = (rfq?: RFQ) => {
    setSelectedRFQ(rfq || null);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setSelectedRFQ(null);
  };

  const handleOpenDeleteDialog = (rfq: RFQ) => {
    setSelectedRFQ(rfq);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedRFQ(null);
  };

  const handleOpenQuotationsDialog = (rfq: RFQ) => {
    setSelectedRFQ(rfq);
    setQuotationsDialogOpen(true);
  };

  const handleCloseQuotationsDialog = () => {
    setQuotationsDialogOpen(false);
    setSelectedRFQ(null);
  };

  const handleDrawerClose = () => {
    setSelectedRFQDetails(null);
    setActivityLogs([]);
    setActivityTotal(0);
    setActivityPage(1);
    setDrawerTab(0);
  };

  React.useEffect(() => {
    loadRFQs();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  if (loading && rfqs.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading RFQs...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <PageHeader
        title="Request for Quotation (RFQ)"
        subtitle="Manage quotation requests for approved purchase requests"
        icon="request_quote"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={() => handleOpenFormDialog()}
            disabled={
              !hasPermission(
                "inventory.rfq.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Create New RFQ
          </Button>
        }
      />

      <Box sx={{ flex: 1, overflow: "hidden", p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <AppSearch
            onSearch={setSearch}
            onFilterChange={setFilters}
            placeholder="Search RFQs..."
            filterGroups={filterGroups}
            defaultFilters={filters}
          />
        </Box>

        {rfqs.length === 0 && !loading ? (
          <EmptyState
            icon="request_quote"
            title="No RFQs Found"
            description="Create your first Request for Quotation from an approved Purchase Request"
            actionText="Create New RFQ"
            onAction={() => handleOpenFormDialog()}
          />
        ) : (
          <AppTable
            data={rfqs}
            columns={MasterColumns}
            loading={loading}
            pagination={{
              total: totalItems,
              page: page,
              pageSize: pageSize,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
            onSort={handleSort}
            sortConfig={{
              field: sortField,
              direction: sortOrder,
            }}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 180 },
        }}
      >
        {menuRFQId &&
          (() => {
            const rfq = rfqs.find((r) => r._id === menuRFQId);
            if (!rfq) return null;

            const config = statusConfig[rfq.status];
            return (
              <>
                {config.canEdit && (
                  <MenuItem
                    onClick={() => {
                      handleOpenFormDialog(rfq);
                      handleMenuClose();
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.rfq.edit",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 2, fontSize: 20 }}>edit</Icon>
                    Edit RFQ
                  </MenuItem>
                )}

                {config.canSend && (
                  <MenuItem
                    onClick={() => handleSendRFQ(menuRFQId)}
                    sx={{ color: "#3B82F6" }}
                    disabled={
                      !hasPermission(
                        "inventory.rfq.send",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 2, fontSize: 20 }}>send</Icon>
                    Send to Suppliers
                  </MenuItem>
                )}

                {config.canManageQuotations && (
                  <MenuItem
                    onClick={() => {
                      handleOpenQuotationsDialog(rfq);
                      handleMenuClose();
                    }}
                    sx={{ color: "#059669" }}
                    disabled={
                      !hasPermission(
                        "inventory.rfq.view",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 2, fontSize: 20 }}>add_business</Icon>
                    Manage Quotations
                  </MenuItem>
                )}

                {config.canCancel && (
                  <MenuItem
                    onClick={() => handleCancelRFQ(menuRFQId)}
                    sx={{ color: "#F59E0B" }}
                    disabled={
                      !hasPermission(
                        "inventory.rfq.cancel",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 2, fontSize: 20 }}>cancel</Icon>
                    Cancel RFQ
                  </MenuItem>
                )}

                {(config.canEdit ||
                  config.canSend ||
                  config.canManageQuotations ||
                  config.canCancel) && <Divider />}

                {config.canDelete && (
                  <MenuItem
                    onClick={() => {
                      handleOpenDeleteDialog(rfq);
                      handleMenuClose();
                    }}
                    sx={{ color: "#DC2626" }}
                    disabled={
                      !hasPermission(
                        "inventory.rfq.delete",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 2, fontSize: 20 }}>delete</Icon>
                    Delete RFQ
                  </MenuItem>
                )}
              </>
            );
          })()}
      </Menu>

      {/* Form Dialog */}
      <RFQFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSave={loadRFQs}
        rfq={selectedRFQ}
        loading={saving}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteRFQ}
        title="Delete RFQ"
        itemName={selectedRFQ?.rfq_number || ""}
        itemDescription="This action cannot be undone and all associated quotations will be permanently deleted."
        loading={false}
      />

      {/* Quotations Dialog */}
      {selectedRFQ && (
        <VendorQuotationsDialog
          open={quotationsDialogOpen}
          onClose={handleCloseQuotationsDialog}
          rfq={selectedRFQ}
          onUpdate={loadRFQs}
        />
      )}

      {/* RFQ Details Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedRFQDetails}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 600,
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {selectedRFQDetails && (
          <>
            {/* Header */}
            <Box sx={{ p: 3, pb: 0 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    borderRadius: "12px",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ color: "white" }}>request_quote</Icon>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    RFQ Details
                  </Typography>
                  <Box>
                    {selectedRFQDetails.status === "draft" ||
                    !selectedRFQDetails.rfq_number ? (
                      <Tooltip
                        title="Send this RFQ to suppliers to generate an RFQ Number."
                        arrow
                      >
                        <Chip
                          label="Not Generated Yet"
                          size="small"
                          sx={{
                            bgcolor: "grey.100",
                            color: "grey.600",
                            fontWeight: 500,
                            fontSize: "0.95em",
                            height: 24,
                            px: 1.5,
                            letterSpacing: 0.2,
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {selectedRFQDetails.rfq_number}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {getStatusChip(selectedRFQDetails.status)}
                <IconButton
                  onClick={handleDrawerClose}
                  sx={{ ml: 1 }}
                  size="small"
                >
                  <Icon>close</Icon>
                </IconButton>
              </Box>

              {/* Tabs */}
              <Tabs
                value={drawerTab}
                onChange={(_, newValue) => setDrawerTab(newValue)}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    minHeight: 48,
                  },
                }}
              >
                <Tab
                  label="Details"
                  icon={<Icon>info</Icon>}
                  iconPosition="start"
                />
                <Tab
                  label={`Activity (${activityTotal})`}
                  icon={<Icon>history</Icon>}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {drawerTab === 0 && (
                <Box sx={{ p: 3 }}>
                  {/* Purchase Request Section */}
                  <Paper sx={{ p: 3, mb: 3, bgcolor: "grey.50" }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
                    >
                      Purchase Request Details
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        sx={{ mb: 0.5 }}
                      >
                        Purchase Request Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedRFQDetails.purchase_request_number}
                      </Typography>
                    </Box>

                    {/* Purchase Request Items */}
                    {selectedRFQDetails.pr_items &&
                      selectedRFQDetails.pr_items.length > 0 && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            sx={{ mb: 2 }}
                          >
                            Items ({selectedRFQDetails.pr_items.length})
                          </Typography>
                          <Box sx={{ display: "grid", gap: 2 }}>
                            {selectedRFQDetails.pr_items.map((item, index) => (
                              <Paper
                                key={index}
                                sx={{
                                  p: 2,
                                  bgcolor: "background.paper",
                                  border: "1px solid",
                                  borderColor: "grey.200",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                      {item.item_name}
                                    </Typography>
                                    {item.reason && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block" }}
                                      >
                                        Reason: {item.reason}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                      ml: 2,
                                    }}
                                  >
                                    <Box sx={{ textAlign: "right" }}>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {item.quantity} {item.uom}
                                      </Typography>
                                    </Box>
                                    {item.urgency && (
                                      <Chip
                                        label={item.urgency}
                                        size="small"
                                        sx={{
                                          height: 24,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          bgcolor:
                                            item.urgency === "high"
                                              ? "#ffebee"
                                              : item.urgency === "medium"
                                              ? "#fff3e0"
                                              : "#e8f5e8",
                                          color:
                                            item.urgency === "high"
                                              ? "#c62828"
                                              : item.urgency === "medium"
                                              ? "#ef6c00"
                                              : "#2e7d32",
                                          border: `1px solid ${
                                            item.urgency === "high"
                                              ? "#ffcdd2"
                                              : item.urgency === "medium"
                                              ? "#ffe0b2"
                                              : "#c8e6c9"
                                          }`,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        </Box>
                      )}
                  </Paper>

                  {/* Suppliers Section */}
                  <Paper sx={{ p: 3, mb: 3, bgcolor: "grey.50" }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
                    >
                      Selected Suppliers (
                      {selectedRFQDetails.selected_suppliers?.length || 0})
                    </Typography>

                    {/* Enhanced supplier display with corrected field names */}
                    {selectedRFQDetails.supplier_details &&
                    selectedRFQDetails.supplier_details.length > 0 ? (
                      <Grid container spacing={2}>
                        {selectedRFQDetails.supplier_details.map(
                          (supplier, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Paper
                                sx={{
                                  p: 2,
                                  bgcolor: "background.paper",
                                  border: "1px solid",
                                  borderColor: "grey.200",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      bgcolor: "primary.100",
                                      borderRadius: "50%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "primary.main",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {(
                                        supplier.name ||
                                        supplier.supplier_name ||
                                        "US"
                                      )
                                        .charAt(0)
                                        .toUpperCase()}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                      {supplier.name ||
                                        supplier.supplier_name ||
                                        "Unknown Supplier"}
                                    </Typography>
                                    {(supplier.contact_email ||
                                      supplier.contact_phone) && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          display: "block",
                                          wordBreak: "break-all",
                                        }}
                                      >
                                        {supplier.contact_email &&
                                          supplier.contact_email}
                                        {supplier.contact_email &&
                                          supplier.contact_phone &&
                                          " • "}
                                        {supplier.contact_phone &&
                                          supplier.contact_phone}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>
                          )
                        )}
                      </Grid>
                    ) : selectedRFQDetails.selected_suppliers &&
                      selectedRFQDetails.selected_suppliers.length > 0 ? (
                      <Box>
                        <Grid container spacing={2}>
                          {selectedRFQDetails.selected_suppliers.map(
                            (supplierId, index) => (
                              <Grid item xs={12} md={6} key={index}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    bgcolor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "grey.200",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: "grey.100",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "grey.600",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {index + 1}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        Supplier {index + 1}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        ID: {supplierId}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                            )
                          )}
                        </Grid>
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: "info.50",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "info.200",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="info.main"
                            sx={{ fontStyle: "italic" }}
                          >
                            Detailed supplier information will be loaded when
                            the RFQ is sent
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "warning.50",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "warning.200",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="warning.main"
                          sx={{ fontStyle: "italic" }}
                        >
                          No suppliers selected
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  {/* Timeline & Details Section */}
                  <Paper sx={{ p: 3, mb: 3, bgcolor: "grey.50" }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 3, color: "text.primary" }}
                    >
                      Timeline & Details
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "background.paper",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            sx={{ mb: 0.5 }}
                          >
                            Due Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedRFQDetails.due_date
                              ? formatDate(selectedRFQDetails.due_date)
                              : "Not set"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "background.paper",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            sx={{ mb: 0.5 }}
                          >
                            Created Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatDate(selectedRFQDetails.created_at)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "background.paper",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            sx={{ mb: 0.5 }}
                          >
                            Quotations Received
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color:
                                selectedRFQDetails.vendor_quotations?.length > 0
                                  ? "success.main"
                                  : "text.primary",
                            }}
                          >
                            {selectedRFQDetails.vendor_quotations?.length || 0}{" "}
                            quotation(s)
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Notes Section */}
                  {selectedRFQDetails.notes && (
                    <Paper sx={{ p: 3, bgcolor: "grey.50" }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
                      >
                        Notes
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "grey.200",
                        }}
                      >
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          {selectedRFQDetails.notes}
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}

              {drawerTab === 1 && (
                <Box sx={{ p: 3 }}>
                  <RFQActivityTimeline
                    activities={activityLogs}
                    loading={activityLoading}
                    total={activityTotal}
                    page={activityPage}
                    pageSize={activityPageSize}
                    onPageChange={handleActivityPageChange}
                    showPagination={true}
                  />
                </Box>
              )}
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
};
